import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ShoppingBag, Calculator, Car, Home } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './LuxuryTaxCalculatorPage/components/CalculationForm';
import ResultsOverview from './LuxuryTaxCalculatorPage/components/ResultsOverview';
import TaxBreakdown from './LuxuryTaxCalculatorPage/components/TaxBreakdown';
import QuickAdjustmentSidebar from './LuxuryTaxCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './LuxuryTaxCalculatorPage/components/ComparisonSidebar';
import TaxInfoSidebar from './LuxuryTaxCalculatorPage/components/TaxInfoSidebar';
import InfoContent from './LuxuryTaxCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  // Общие параметры
  taxObjectType: 'car', // 'car' или 'property'
  year: new Date().getFullYear().toString(),
  
  // Параметры для автомобилей
  carCost: '3000000',
  carPower: '150',
  carAge: '0',
  carType: 'standard', // 'standard', 'luxury', 'commercial'
  isElectric: false,
  isFirstRegistration: true,
  
  // Параметры для недвижимости
  propertyCost: '10000000',
  propertyType: 'apartment', // 'apartment', 'house', 'land', 'commercial'
  propertyArea: '80',
  cadastralValue: '8000000',
  ownershipType: 'individual', // 'individual', 'shared', 'joint'
  ownershipShare: '100',
  isOnlyProperty: true,
  
  // Параметры налоговых льгот
  hasPrivileges: false,
  privilegeType: 'none', // 'none', 'veteran', 'disability', 'pensioner', 'large_family'
  hasChildrenShare: false,
  childrenSharePercent: '0',
  
  // Местоположение
  region: 'moscow', // код региона
  municipality: 'central',
};

// Определение опций для типов автомобилей
export const carTypes = [
  { id: 'standard', name: 'Стандартный легковой автомобиль', defaultCoefficient: 1 },
  { id: 'luxury', name: 'Премиальный автомобиль (из списка МинПромТорга)', defaultCoefficient: 1.5 },
  { id: 'commercial', name: 'Коммерческий автомобиль', defaultCoefficient: 0.8 }
];

// Определение опций для типов недвижимости
export const propertyTypes = [
  { id: 'apartment', name: 'Квартира', defaultRate: 0.1 },
  { id: 'house', name: 'Жилой дом', defaultRate: 0.2 },
  { id: 'land', name: 'Земельный участок', defaultRate: 0.3 },
  { id: 'commercial', name: 'Коммерческая недвижимость', defaultRate: 2 }
];

// Определение опций для типов налоговых льгот
export const privilegeTypes = [
  { id: 'none', name: 'Нет льгот', discountPercent: 0 },
  { id: 'veteran', name: 'Ветеран боевых действий', discountPercent: 100 },
  { id: 'disability', name: 'Инвалид I или II группы', discountPercent: 100 },
  { id: 'pensioner', name: 'Пенсионер', discountPercent: 100 },
  { id: 'large_family', name: 'Многодетная семья', discountPercent: 50 }
];

/**
 * LuxuryTaxCalculatorPage Component - Калькулятор налога на роскошь для автомобилей и недвижимости.
 */
const LuxuryTaxCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('luxury-tax-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор налога на роскошь | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте налог на роскошь для автомобилей и недвижимости в России с учетом всех коэффициентов, льгот и региональных ставок. Получите подробный расчет и рекомендации по снижению налоговой нагрузки.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [taxDetails, setTaxDetails] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalculationsData = localStorage.getItem('savedTaxCalculations');
    if (savedCalculationsData) {
      try {
        setSavedCalculations(JSON.parse(savedCalculationsData));
      } catch (e) {
        console.error("Error loading saved calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'car':
          newState.taxObjectType = 'car';
          break;
        case 'property':
          newState.taxObjectType = 'property';
          break;
        case 'luxury-cars':
          newState.taxObjectType = 'car';
          newState.carType = 'luxury';
          newState.carCost = '10000000';
          break;
        case 'premium-property':
          newState.taxObjectType = 'property';
          newState.propertyCost = '100000000';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setTaxDetails([]);
    setErrors({});

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalc = {
      id: Date.now().toString(),
      name: name || `Расчет ${formData.taxObjectType === 'car' ? 'автомобиля' : 'недвижимости'} от ${new Date().toLocaleDateString()}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      taxObjectType: formData.taxObjectType
    };

    const updatedSavedCalcs = [...savedCalculations, newSavedCalc];
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedTaxCalculations', JSON.stringify(updatedSavedCalcs));

    return newSavedCalc;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalcs = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedTaxCalculations', JSON.stringify(updatedSavedCalcs));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calcToLoad = savedCalculations.find(calc => calc.id === id);
    if (calcToLoad) {
      setFormData(calcToLoad.formData);
      setResults(calcToLoad.results);
      // При необходимости можно сгенерировать детали налога заново здесь
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <ShoppingBag className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор налога на роскошь'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
                formData={formData}
                setFormData={setFormData}
                errors={errors || {}}  // Убедимся, что всегда передаем объект
                setErrors={setErrors}
                isCalculating={isCalculating}
                setIsCalculating={setIsCalculating}
                results={results}
                setResults={setResults}
                setTaxDetails={setTaxDetails}  // Передаем функцию установки, а не сам массив
                taxDetails={taxDetails || []}  // Гарантируем, что передаем массив
                carTypes={carTypes}
                propertyTypes={propertyTypes}
                privilegeTypes={privilegeTypes}
                saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <TaxBreakdown 
                  taxDetails={taxDetails} 
                  results={results}
                  taxObjectType={formData.taxObjectType}
                />
              </>
            )}

            {/* --- Информационный контент о налоге на роскошь --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель сравнения расчетов --- */}
            <ComparisonSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Информационная панель о налоге --- */}
            <TaxInfoSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Tax Details Table */
        .tax-details-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .tax-details-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .tax-details-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .tax-details-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .tax-details-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .tax-details-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .tax-details-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .tax-details-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .tax-details-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default LuxuryTaxCalculatorPage;